import type { TFunction } from 'i18next';

export interface WarningInfo {
  key: string;
  params?: Record<string, string | number>;
  nested?: Record<string, WarningInfo | string>;
}

export interface ValidationResult {
  isValid: boolean;
  error?: string;
  warning?: WarningInfo;
  warnings?: WarningInfo[];
}

export function renderWarning(t: TFunction, w: WarningInfo): string {
  const resolvedParams: Record<string, unknown> = { ...(w.params || {}) };
  if (w.nested) {
    for (const [k, v] of Object.entries(w.nested)) {
      resolvedParams[k] = typeof v === 'string' ? v : renderWarning(t, v);
    }
  }
  return t(w.key, resolvedParams) as string;
}

// File size constants
const BYTES_PER_MB = 1024 * 1024;
import { DEFAULT_MAX_FILE_SIZE_MB } from '@/hooks/useFileSizeLimit';

// File name constants
const MAX_FILENAME_LENGTH = 255;

// Validation thresholds
const MAX_ROWS_WARNING = 10000000;
const MAX_COLUMNS_WARNING = 10000000;

// Performance optimization constants
const LARGE_FILE_THRESHOLD_ROWS = 100000; // Use sampling for files with > 100k rows
const SAMPLE_SIZE_FIRST = 500; // Validate first 500 rows
const SAMPLE_SIZE_MIDDLE = 250; // Sample 250 rows from middle
const SAMPLE_SIZE_LAST = 250; // Validate last 250 rows
const DELIMITER_DETECTION_SAMPLE_LINES = 5; // Check first 5 lines for delimiter

// Allowed file types
const ALLOWED_EXTENSIONS = ['.csv', '.txt'];
const ALLOWED_MIME_TYPES = ['text/csv', 'text/plain', 'application/csv'];

/**
 * Generates sample indices for large file validation
 * Strategy: First N rows + random middle samples + last N rows
 * @param totalRows Total number of rows in file
 * @returns Array of row indices to validate
 */
function getSampleIndices(totalRows: number): number[] {
  if (totalRows <= LARGE_FILE_THRESHOLD_ROWS) {
    // Small file: validate all rows
    return Array.from({ length: totalRows }, (_, i) => i);
  }

  const indices = new Set<number>();

  // First rows (catches early structure issues)
  for (let i = 0; i < Math.min(SAMPLE_SIZE_FIRST, totalRows); i++) {
    indices.add(i);
  }

  // Last rows (catches end-of-file issues)
  for (let i = Math.max(0, totalRows - SAMPLE_SIZE_LAST); i < totalRows; i++) {
    indices.add(i);
  }

  // Random middle samples (catches anomalies)
  const middleStart = SAMPLE_SIZE_FIRST;
  const middleEnd = totalRows - SAMPLE_SIZE_LAST;
  const middleRange = middleEnd - middleStart;

  if (middleRange > 0) {
    for (let i = 0; i < SAMPLE_SIZE_MIDDLE; i++) {
      const randomIndex = middleStart + Math.floor(Math.random() * middleRange);
      indices.add(randomIndex);
    }
  }

  return Array.from(indices).sort((a, b) => a - b);
}

/**
 * Validates file type by checking extension and MIME type
 * @param file File to validate
 * @param t Translation function
 * @returns Validation result with error or warning if issues found
 */
export function validateFileType(file: File, t: TFunction): ValidationResult {
  const fileName = file.name.toLowerCase();
  const hasValidExtension = ALLOWED_EXTENSIONS.some(ext => fileName.endsWith(ext));

  if (!hasValidExtension) {
    return {
      isValid: false,
      error: t('validation.invalidFileType', { extensions: ALLOWED_EXTENSIONS.join(', ') })
    };
  }

  const hasValidMimeType = ALLOWED_MIME_TYPES.includes(file.type);
  if (!hasValidMimeType && file.type !== '') {
    return {
      isValid: true,
      warning: { key: 'validation.unusualMimeType', params: { mimeType: file.type } }
    };
  }

  return { isValid: true };
}

/**
 * Validates file size against maximum limit
 * @param file File to validate
 * @param t Translation function
 * @param maxFileSizeMB Optional max file size in MB (defaults to subscription plan limit)
 * @returns Validation result with error if file is too large or empty
 */
export function validateFileSize(
  file: File, 
  t: TFunction, 
  maxFileSizeMB: number = DEFAULT_MAX_FILE_SIZE_MB
): ValidationResult {
  if (file.size === 0) {
    return {
      isValid: false,
      error: t('validation.fileEmpty')
    };
  }

  // A negative limit is the plan's "unlimited" sentinel (max_file_size_mb = -1),
  // matching useFileSizeLimit. Multiplying it out would give a negative budget
  // that rejects every file.
  const maxFileSizeBytes = maxFileSizeMB < 0 ? Infinity : maxFileSizeMB * BYTES_PER_MB;
  if (file.size > maxFileSizeBytes) {
    const fileSizeMB = (file.size / BYTES_PER_MB).toFixed(2);
    return {
      isValid: false,
      error: t('validation.fileTooLarge', { sizeMB: fileSizeMB, maxSizeMB: maxFileSizeMB })
    };
  }

  return { isValid: true };
}

/**
 * Detects the most likely delimiter by sampling multiple lines
 * Improved version that checks multiple lines instead of just the first
 * @param lines Array of content lines to analyze
 * @returns Most likely delimiter character
 */
export function detectDelimiter(lines: string[]): string {
  const delimiters = [';', ',', '\t', '|'];
  const sampleLines = lines.slice(0, Math.min(DELIMITER_DETECTION_SAMPLE_LINES, lines.length));

  if (sampleLines.length === 0) return ';';

  // Count occurrences across multiple lines for more robust detection
  const delimiterScores = delimiters.map(delimiter => {
    const counts = sampleLines.map(line =>
      (line.match(new RegExp(`\\${delimiter}`, 'g')) || []).length
    );

    // Average count across sampled lines
    const avgCount = counts.reduce((sum, count) => sum + count, 0) / counts.length;

    // Consistency score (lower variance is better)
    const variance = counts.reduce((sum, count) => sum + Math.pow(count - avgCount, 2), 0) / counts.length;

    return {
      delimiter,
      avgCount,
      consistency: avgCount > 0 ? avgCount / (1 + variance) : 0
    };
  });

  // Choose delimiter with best consistency score
  const bestMatch = delimiterScores.reduce((best, current) =>
    current.consistency > best.consistency ? current : best
  );

  return bestMatch.avgCount > 0 ? bestMatch.delimiter : ';';
}

/**
 * Validates CSV structure (basic format check)
 * @param content Full file content as string
 * @param t Translation function
 * @returns Validation result with error or warning if issues found
 */
export function validateCSVStructure(content: string, t: TFunction): ValidationResult {
  if (!content || content.trim().length === 0) {
    return {
      isValid: false,
      error: t('validation.contentEmpty')
    };
  }

  const lines = content.split('\n').filter(line => line.trim().length > 0);

  if (lines.length === 0) {
    return {
      isValid: false,
      error: t('validation.noDataRows')
    };
  }

  if (lines.length === 1) {
    return {
      isValid: true,
      warning: { key: 'validation.singleRowWarning' }
    };
  }

  const delimiter = detectDelimiter(lines);
  const firstLineColumns = lines[0].split(delimiter).length;

  if (firstLineColumns === 1 && lines.length > 1) {
    return {
      isValid: false,
      error: t('validation.noDelimiterDetected')
    };
  }

  return { isValid: true };
}

/**
 * Validates row consistency using intelligent sampling for large files
 * For files > 100k rows, samples strategically instead of checking all rows
 * @param lines Array of all lines in the file
 * @param delimiter Column delimiter character
 * @param t Translation function
 * @returns Validation result with warning if inconsistencies found
 */
export function validateRowConsistency(lines: string[], delimiter: string, t: TFunction): ValidationResult {
  if (lines.length === 0) {
    return {
      isValid: false,
      error: t('validation.noDataRows')
    };
  }

  const totalRows = lines.length;
  const sampleIndices = getSampleIndices(totalRows);
  const usingSampling = totalRows > LARGE_FILE_THRESHOLD_ROWS;

  // Get column counts for sampled rows only
  const columnCounts = sampleIndices.map(index => lines[index].split(delimiter).length);

  // Use reduce to avoid stack overflow with large arrays
  const maxColumns = columnCounts.reduce((max, count) => Math.max(max, count), 0);
  const minColumns = columnCounts.reduce((min, count) => Math.min(min, count), Infinity);

  if (maxColumns !== minColumns) {
    const inconsistentCount = columnCounts.filter(count => count !== maxColumns).length;
    const samplingNote: WarningInfo | string = usingSampling
      ? {
          key: 'validation.samplingNote',
          params: { sampleSize: sampleIndices.length, totalRows }
        }
      : '';

    return {
      isValid: true,
      warning: {
        key: 'validation.inconsistentColumns',
        params: { count: inconsistentCount },
        nested: { samplingNote }
      }
    };
  }

  if (maxColumns > MAX_COLUMNS_WARNING) {
    return {
      isValid: true,
      warning: { key: 'validation.tooManyColumns', params: { count: maxColumns } }
    };
  }

  return { isValid: true };
}

/**
 * Validates performance-related concerns (row count)
 * Efficiently counts rows without processing all content
 * @param lines Array of all lines in the file
 * @returns Validation result with warning for very large files
 */
export function validatePerformance(lines: string[]): ValidationResult {
  const rowCount = lines.length;

  if (rowCount > MAX_ROWS_WARNING) {
    return {
      isValid: true,
      warning: { key: 'validation.tooManyRows', params: { count: rowCount } }
    };
  }

  return { isValid: true };
}

/**
 * Sanitizes file name to prevent security issues
 * Removes path traversal attempts, path separators, and dangerous characters
 * @param fileName Original file name
 * @returns Sanitized file name safe for use
 */
export function sanitizeFileName(fileName: string): string {
  // Remove path traversal attempts
  let sanitized = fileName.replace(/\.\./g, '');

  // Remove path separators and potentially dangerous characters
  sanitized = sanitized.replace(/[/\\<>:"|?*]/g, '');

  // Note: whitespace is intentionally NOT trimmed here — this function runs on
  // every keystroke (see DownloadModal), and trimming per-keystroke strips a
  // trailing space as the user types it, making multi-word names untypeable.
  // Trimming happens once at confirm-time instead (DownloadModal.getFinalFileName /
  // handleConfirm).

  // Limit length
  if (sanitized.length > MAX_FILENAME_LENGTH) {
    const extension = sanitized.substring(sanitized.lastIndexOf('.'));
    sanitized = sanitized.substring(0, MAX_FILENAME_LENGTH - extension.length) + extension;
  }

  return sanitized;
}

/**
 * Comprehensive file validation combining all checks
 * Optimized for large files using sampling strategies
 * @param file File to validate
 * @param t Translation function
 * @param maxFileSizeMB Optional max file size in MB (defaults to subscription plan limit)
 * @returns Promise resolving to validation result with any errors or warnings
 */
export async function validateFile(
  file: File, 
  t: TFunction, 
  maxFileSizeMB?: number
): Promise<ValidationResult> {
  // Phase 1: File-level validations (fast, no content reading)
  const typeValidation = validateFileType(file, t);
  if (!typeValidation.isValid) {
    return typeValidation;
  }

  const sizeValidation = validateFileSize(file, t, maxFileSizeMB);
  if (!sizeValidation.isValid) {
    return sizeValidation;
  }

  // Phase 2: Content validation (reads file into memory)
  const content = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as string || '');
    reader.onerror = () => reject(new Error(t('validation.readError')));
    reader.readAsText(file);
  });

  // Parse lines once and reuse for all validations (memory optimization)
  const lines = content.split('\n').filter(line => line.trim().length > 0);

  // Structure validation
  const structureValidation = validateCSVStructure(content, t);
  if (!structureValidation.isValid) {
    return structureValidation;
  }

  // Detect delimiter using improved multi-line detection
  const delimiter = detectDelimiter(lines);

  // Consistency validation with intelligent sampling
  const consistencyValidation = validateRowConsistency(lines, delimiter, t);

  // Performance validation
  const performanceValidation = validatePerformance(lines);

  // Combine all warnings
  const warnings: WarningInfo[] = [];
  if (typeValidation.warning) warnings.push(typeValidation.warning);
  if (structureValidation.warning) warnings.push(structureValidation.warning);
  if (consistencyValidation.warning) warnings.push(consistencyValidation.warning);
  if (performanceValidation.warning) warnings.push(performanceValidation.warning);

  return {
    isValid: true,
    warnings: warnings.length > 0 ? warnings : undefined
  };
}
